/**
 * Timeline Component for Reels video editor
 * Multi-track timeline with clips, playhead, and drag-drop support
 */

import React, { useRef, useState, useCallback, useEffect } from 'react';
import { Plus, Lock, Unlock, Eye, EyeOff, Volume2, VolumeX, Trash2, Scissors, ChevronUp, ChevronDown } from 'lucide-react';
import { useProject, useUI, getProjectDuration } from '../stores/projectStore';
import { timeToPixels, pixelsToTime, formatDuration, snapToGrid } from '../utils/timeUtils';

const PIXELS_PER_SECOND = 50;
const TRACK_HEIGHT = 60;
const HEADER_WIDTH = 180;

const Timeline = () => {
  const { project, actions: projectActions } = useProject();
  const { ui, actions: uiActions } = useUI();
  const timelineRef = useRef(null);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [dragState, setDragState] = useState(null);

  const duration = getProjectDuration(project) || 30;
  const timelineWidth = timeToPixels(duration + 10, ui.zoom, PIXELS_PER_SECOND);

  // Handle scroll
  const handleScroll = (e) => {
    setScrollLeft(e.target.scrollLeft);
  };

  // Handle timeline click to seek
  const handleTimelineClick = (e) => {
    if (dragState) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left - HEADER_WIDTH + scrollLeft;
    const time = Math.max(0, pixelsToTime(x, ui.zoom, PIXELS_PER_SECOND));
    uiActions.setCurrentTime(time);
  };

  // Handle playhead drag
  const handlePlayheadMouseDown = (e) => {
    e.stopPropagation();
    setDragState({ type: 'playhead' });
  };

  // Handle clip drag start
  const handleClipMouseDown = (e, trackId, clip) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;

    setDragState({
      type: 'clip',
      trackId,
      clipId: clip.id,
      offsetX,
      originalStartTime: clip.startTime,
    });
    uiActions.setSelectedClip(clip.id);
    uiActions.setDragging(true);
  };

  // Handle trim drag
  const handleTrimMouseDown = (e, trackId, clip, edge) => {
    e.stopPropagation();
    setDragState({
      type: 'trim',
      edge,
      trackId,
      clipId: clip.id,
      originalStartTime: clip.startTime,
      originalDuration: clip.duration,
      originalInPoint: clip.inPoint || 0,
    });
    uiActions.setDragging(true);
  };

  // Global mouse move handler
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!dragState) return;

      const timeline = timelineRef.current;
      if (!timeline) return;

      const rect = timeline.getBoundingClientRect();
      const x = e.clientX - rect.left - HEADER_WIDTH + scrollLeft;
      const time = Math.max(0, pixelsToTime(x, ui.zoom, PIXELS_PER_SECOND));
      const snappedTime = ui.snapEnabled ? snapToGrid(time, 0.1) : time;

      if (dragState.type === 'playhead') {
        uiActions.setCurrentTime(snappedTime);
      } else if (dragState.type === 'clip') {
        const newStartTime = Math.max(0, snappedTime - pixelsToTime(dragState.offsetX, ui.zoom, PIXELS_PER_SECOND));
        projectActions.updateClip(dragState.trackId, dragState.clipId, { startTime: newStartTime });
      } else if (dragState.type === 'trim') {
        if (dragState.edge === 'left') {
          const delta = snappedTime - dragState.originalStartTime;
          const newStartTime = dragState.originalStartTime + delta;
          const newDuration = dragState.originalDuration - delta;
          const newInPoint = dragState.originalInPoint + delta;

          if (newDuration > 0.1) {
            projectActions.updateClip(dragState.trackId, dragState.clipId, {
              startTime: newStartTime,
              duration: newDuration,
              inPoint: newInPoint,
            });
          }
        } else {
          const delta = snappedTime - (dragState.originalStartTime + dragState.originalDuration);
          const newDuration = Math.max(0.1, dragState.originalDuration + delta);
          projectActions.updateClip(dragState.trackId, dragState.clipId, { duration: newDuration });
        }
      }
    };

    const handleMouseUp = () => {
      if (dragState) {
        setDragState(null);
        uiActions.setDragging(false);
      }
    };

    if (dragState) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragState, ui.zoom, ui.snapEnabled, scrollLeft, projectActions, uiActions]);

  // Generate time markers
  const renderTimeRuler = () => {
    const markers = [];
    const step = ui.zoom < 0.5 ? 5 : ui.zoom < 1 ? 2 : 1;

    for (let i = 0; i <= duration + 10; i += step) {
      const x = timeToPixels(i, ui.zoom, PIXELS_PER_SECOND);
      markers.push(
        <div
          key={i}
          className="absolute top-0 h-full"
          style={{ left: x }}
        >
          <div className="text-xs text-slate-500 whitespace-nowrap pl-1">{formatDuration(i)}</div>
          <div className="w-px h-2 bg-slate-700 mt-1" />
        </div>
      );
    }
    return markers;
  };

  // Render a single clip
  const renderClip = (track, clip) => {
    const left = timeToPixels(clip.startTime, ui.zoom, PIXELS_PER_SECOND);
    const width = timeToPixels(clip.duration, ui.zoom, PIXELS_PER_SECOND);
    const isSelected = ui.selectedClipId === clip.id;

    const clipColor = track.type === 'video' ? 'bg-teal-600' : track.type === 'audio' ? 'bg-amber-500' : 'bg-violet-600';

    return (
      <div
        key={clip.id}
        className={`absolute top-1 bottom-1 rounded cursor-pointer group ${clipColor} ${isSelected ? 'ring-2 ring-white ring-offset-1 ring-offset-slate-900' : ''}`}
        style={{ left, width: Math.max(width, 20) }}
        onMouseDown={(e) => handleClipMouseDown(e, track.id, clip)}
      >
        {/* Left trim handle */}
        <div
          className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white/30 rounded-l"
          onMouseDown={(e) => handleTrimMouseDown(e, track.id, clip, 'left')}
        />

        {/* Clip content */}
        <div className="px-2 py-1 overflow-hidden h-full flex items-center">
          <span className="text-xs text-white truncate">
            {clip.name || `Clip ${clip.id.slice(-4)}`}
          </span>
        </div>

        {/* Right trim handle */}
        <div
          className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white/30 rounded-r"
          onMouseDown={(e) => handleTrimMouseDown(e, track.id, clip, 'right')}
        />

        {/* Effects indicator */}
        {clip.effects && clip.effects.length > 0 && (
          <div className="absolute top-1 right-1 w-2 h-2 rounded-full bg-yellow-400" title="Has effects" />
        )}
      </div>
    );
  };

  // Render text/overlay clips on a separate track
  const renderOverlayClips = () => {
    return (
      <div className="flex border-b border-slate-800" style={{ height: TRACK_HEIGHT }}>
        {/* Track header */}
        <div className="flex-shrink-0 px-3 py-2 bg-slate-900/50 border-r border-slate-800 flex items-center gap-2" style={{ width: HEADER_WIDTH }}>
          <span className="text-sm text-slate-300 truncate flex-1">Text & Graphics</span>
        </div>

        {/* Track content */}
        <div className="flex-1 relative bg-slate-900/30" style={{ width: timelineWidth }}>
          {project.textClips.map((clip) => {
            const left = timeToPixels(clip.startTime, ui.zoom, PIXELS_PER_SECOND);
            const width = timeToPixels(clip.duration, ui.zoom, PIXELS_PER_SECOND);
            const isSelected = ui.selectedTextId === clip.id;

            return (
              <div
                key={clip.id}
                className={`absolute top-1 bottom-1 rounded cursor-pointer bg-violet-600 ${isSelected ? 'ring-2 ring-white' : ''}`}
                style={{ left, width: Math.max(width, 20) }}
                onClick={() => uiActions.setSelectedText(clip.id)}
              >
                <div className="px-2 py-1 overflow-hidden h-full flex items-center">
                  <span className="text-xs text-white truncate">{clip.text || 'Text'}</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-slate-900/90 border-t border-slate-800/80 backdrop-blur">
      {/* Timeline toolbar */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-800/80 bg-slate-900/70">
        <div className="flex items-center gap-2">
          <button
            onClick={() => projectActions.addTrack({
              id: `video-${Date.now()}`,
              type: 'video',
              name: `Video ${project.tracks.filter(t => t.type === 'video').length + 1}`,
              clips: [],
              muted: false,
              locked: false,
              visible: true,
            })}
            className="flex items-center gap-1 px-2 py-1 text-xs text-slate-400 hover:text-white hover:bg-slate-800 rounded transition-colors"
          >
            <Plus className="w-3 h-3" />
            Video Track
          </button>
          <button
            onClick={() => projectActions.addTrack({
              id: `audio-${Date.now()}`,
              type: 'audio',
              name: `Audio ${project.tracks.filter(t => t.type === 'audio').length + 1}`,
              clips: [],
              muted: false,
              locked: false,
              volume: 1,
            })}
            className="flex items-center gap-1 px-2 py-1 text-xs text-slate-400 hover:text-white hover:bg-slate-800 rounded transition-colors"
          >
            <Plus className="w-3 h-3" />
            Audio Track
          </button>
        </div>

        <div className="flex items-center gap-2">
          {/* Snap toggle */}
          <button
            onClick={() => uiActions.setSnapEnabled(!ui.snapEnabled)}
            className={`px-2 py-1 text-xs rounded transition-colors ${ui.snapEnabled ? 'bg-teal-600 text-white' : 'text-slate-400 hover:bg-slate-800'}`}
          >
            Snap
          </button>

          {/* Zoom controls */}
          <div className="flex items-center gap-1">
            <button
              onClick={() => uiActions.setZoom(Math.max(0.25, ui.zoom - 0.25))}
              className="p-1 text-slate-400 hover:text-white hover:bg-slate-800 rounded"
            >
              <ChevronDown className="w-4 h-4" />
            </button>
            <span className="text-xs text-slate-500 w-12 text-center">{Math.round(ui.zoom * 100)}%</span>
            <button
              onClick={() => uiActions.setZoom(Math.min(4, ui.zoom + 0.25))}
              className="p-1 text-slate-400 hover:text-white hover:bg-slate-800 rounded"
            >
              <ChevronUp className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Timeline content */}
      <div
        ref={timelineRef}
        className="flex-1 overflow-auto"
        onScroll={handleScroll}
        onClick={handleTimelineClick}
      >
        {/* Time ruler */}
        <div className="flex border-b border-slate-800 bg-slate-900/70 sticky top-0 z-10" style={{ height: 28 }}>
          <div className="flex-shrink-0" style={{ width: HEADER_WIDTH }} />
          <div className="flex-1 relative" style={{ width: timelineWidth }}>
            {renderTimeRuler()}

            {/* Playhead in ruler */}
            <div
              className="absolute top-0 bottom-0 w-px bg-rose-500 cursor-ew-resize z-20"
              style={{ left: timeToPixels(ui.currentTime, ui.zoom, PIXELS_PER_SECOND) }}
              onMouseDown={handlePlayheadMouseDown}
            >
              <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-rose-500 rotate-45 transform" />
            </div>
          </div>
        </div>

        {/* Text/Graphics track */}
        {(project.textClips.length > 0 || project.shapeClips.length > 0) && renderOverlayClips()}

        {/* Tracks */}
        {project.tracks.map((track) => (
          <div key={track.id} className="flex border-b border-slate-800" style={{ height: TRACK_HEIGHT }}>
            {/* Track header */}
            <div className="flex-shrink-0 px-3 py-2 bg-slate-900/50 border-r border-slate-800 flex items-center gap-2" style={{ width: HEADER_WIDTH }}>
              <div className={`w-2 h-2 rounded-full ${track.type === 'video' ? 'bg-teal-400' : 'bg-amber-400'}`} />
              <span className="text-sm text-slate-300 truncate flex-1">{track.name}</span>

              <div className="flex items-center gap-1">
                {track.type === 'audio' ? (
                  <button
                    onClick={() => projectActions.updateTrack(track.id, { muted: !track.muted })}
                    className={`p-1 rounded ${track.muted ? 'text-red-400' : 'text-slate-400 hover:text-white'}`}
                  >
                    {track.muted ? <VolumeX className="w-3 h-3" /> : <Volume2 className="w-3 h-3" />}
                  </button>
                ) : (
                  <button
                    onClick={() => projectActions.updateTrack(track.id, { visible: !track.visible })}
                    className={`p-1 rounded ${!track.visible ? 'text-red-400' : 'text-slate-400 hover:text-white'}`}
                  >
                    {track.visible ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                  </button>
                )}

                <button
                  onClick={() => projectActions.updateTrack(track.id, { locked: !track.locked })}
                  className={`p-1 rounded ${track.locked ? 'text-amber-400' : 'text-slate-400 hover:text-white'}`}
                >
                  {track.locked ? <Lock className="w-3 h-3" /> : <Unlock className="w-3 h-3" />}
                </button>
              </div>
            </div>

            {/* Track clips area */}
            <div className="flex-1 relative bg-slate-900/30" style={{ width: timelineWidth }}>
              {track.clips.map((clip) => renderClip(track, clip))}

              {/* Playhead line */}
              <div
                className="absolute top-0 bottom-0 w-px bg-rose-500 pointer-events-none z-10"
                style={{ left: timeToPixels(ui.currentTime, ui.zoom, PIXELS_PER_SECOND) }}
              />
            </div>
          </div>
        ))}

        {/* Empty state */}
        {project.tracks.every(t => t.clips.length === 0) && (
          <div className="flex items-center justify-center h-32 text-slate-500 text-sm">
            Drag media here to get started
          </div>
        )}
      </div>
    </div>
  );
};

export default Timeline;
